import React, {useState} from 'react';
import usePortal from 'react-useportal';
import {
    Icon,
    PriorityIcon,
    Scrollbar,
    Input,
    Text,
    DropdownV2
} from '@priority-software/priority-style-react';
import {DropdownProps} from './StatusField';
import {StatusListItem} from './StatusListItem';
import {MIN_ITEMS_FOR_SCROLLBAR} from '../../consts';
import {isRTL} from '../../../../Localization';
import styles from '../../styles/ToDoListConent.module.scss';
import {priority} from '@priority-software/priority-proto-api/lib';
import texts from '../../texts';
import {useKeyboardNavigation} from '../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {keyCodes} from '../../../../constants';

type StatusDropdownProps = {
    dropdownProps: DropdownProps;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    saveUpdate: () => void;
    updateTaskStatus: (value: string) => void;
    setIsScrollbarDisabled: (value: boolean) => void;
};

export const StatusDropdown = ({
    dropdownProps,
    isOpen,
    setIsOpen,
    saveUpdate,
    updateTaskStatus,
    setIsScrollbarDisabled
}: StatusDropdownProps) => {
    const [formStatuses, setFormStatuses] = useState<priority.netitems.form.ISelectValues[]>(
        dropdownProps.formStatuses
    );
    const [searchValue, setSearchValue] = useState<string>('');

    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength: formStatuses.length,
        pageSize: 1,
        onSelect: () => handleSelect(formStatuses[focusedIndex].Value)
    });

    const {Portal} = usePortal();

    const handleClose = async () => {
        isOpen && setIsOpen(false);
        await saveUpdate();
        setSearchValue('');
        setIsScrollbarDisabled(false);
    };

    const handleSelect = async (value: string) => {
        setIsOpen(false);
        await updateTaskStatus(value);
        await handleClose();
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        const filteredStatues = dropdownProps.formStatuses.filter(status =>
            status.Value.toLowerCase().includes(value.toLowerCase())
        );
        setFormStatuses(filteredStatues);
    };

    const handleResetSearch = () => {
        setSearchValue('');
        setFormStatuses(dropdownProps.formStatuses);
    };

    const handleKeyDown = async (e: KeyboardEvent) => {
        if (e.keyCode === keyCodes.TAB) {
            await handleClose();
        } else {
            handleKeyboardNavigation(e);
        }
    };

    return (
        <div className={styles.portalContainer}>
            <Portal>
                <DropdownV2
                    isOpen={isOpen}
                    componentClasses={styles.dropdownContainer}
                    contentComponentClasses={styles.dropdownContent}
                    contentStyle={dropdownProps.contentStyle}
                    onClose={handleClose}
                    onKeyDown={handleKeyDown}
                    focusOnOpen
                >
                    {dropdownProps.formStatuses?.length > MIN_ITEMS_FOR_SCROLLBAR && (
                        <Input
                            placeholder={texts.search_placeholder}
                            onChange={handleSearch}
                            roundedCorners
                            componentClasses={styles.statusSearchInput}
                            value={searchValue}
                        >
                            {!!searchValue.length ? (
                                <Icon
                                    icon={PriorityIcon.x}
                                    iconColor="primary-700"
                                    onClick={handleResetSearch}
                                />
                            ) : (
                                <Icon icon={PriorityIcon.searchThin} iconColor="primary-700" />
                            )}
                        </Input>
                    )}
                    {formStatuses?.length > 0 && (
                        <Scrollbar
                            autoHeight={true}
                            autoHeightMax={dropdownProps.scrollAreaMaxHeight}
                            autoHide={false}
                            componentClasses={styles.statusesScrollbar}
                            rtl={isRTL()}
                        >
                            {formStatuses.map((status, idx) => {
                                return (
                                    <StatusListItem
                                        key={status.Value}
                                        isFocused={focusedIndex === idx}
                                        isLast={idx === formStatuses.length - 1}
                                        handleSelect={handleSelect}
                                        value={status.Value}
                                    />
                                );
                            })}
                        </Scrollbar>
                    )}
                    {formStatuses?.length === 0 && (
                        <Text
                            skin="paragraph-2"
                            textColor="primary-700"
                            componentClasses={styles.noStatusesTxt}
                        >
                            {texts.statuses_no_results}
                        </Text>
                    )}
                </DropdownV2>
            </Portal>
        </div>
    );
};
